import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_AddEditData } from "../../store/Functions";
import { API_WEB_URLS } from "../../constants/constAPI";
import PageTitle from "../layouts/PageTitle";
import { toast } from "react-toastify";
import useSession from "../utils/useSession";

// ─── Column Definitions ───────────────────────────────────────────────────────
const COLS = [
  { key: "Name",            label: "Department Name *", type: "text",    w: 220 },
  { key: "F_FirmMaster",    label: "Firm",              type: "select",  optKey: "FirmMaster",   w: 180, placeholder: "-- Select Firm --" },
  { key: "F_BranchMaster",  label: "Branch",            type: "select",  optKey: "BranchMaster", w: 180, placeholder: "-- Select Branch --" },
  { key: "Ordering",        label: "Ordering",          type: "number",  w: 90 },
  { key: "IsSundayInclude", label: "Sunday Include",    type: "boolean", w: 130, placeholder: "-- Select --" },
  { key: "IsDirectWages",   label: "Direct Wages",      type: "boolean", w: 120, placeholder: "-- Select --" },
  { key: "IsNotInUse",      label: "Not In Use",        type: "boolean", w: 110, placeholder: "-- Select --" },
];

const emptyRow = (firmId = "", branchId = "") => {
  const row = Object.fromEntries(COLS.map((c) => [c.key, c.type === "boolean" ? "false" : ""]));
  return { Id: 0, ...row, F_FirmMaster: firmId ? String(firmId) : "", F_BranchMaster: branchId ? String(branchId) : "" };
};

// Map an API department object → grid row format
const apiRowToGridRow = (r) => ({
  Id: r.Id ?? 0,
  ...Object.fromEntries(COLS.map((col) => {
    let val = r[col.key];
    if (col.type === "boolean") val = val === true || val === "true" ? "true" : "false";
    else if (col.type === "date" && val) val = String(val).substring(0, 10);
    else val = val === null || val === undefined ? "" : String(val);
    return [col.key, val];
  }))
});

// ─── Pure helpers ─────────────────────────────────────────────────────────────
const getSelRange = (foc, anc) => ({
  r1: Math.min(foc.r, anc.r), r2: Math.max(foc.r, anc.r),
  c1: Math.min(foc.c, anc.c), c2: Math.max(foc.c, anc.c),
});
const colLetter = (c) => {
  let s = ""; let n = c + 1;
  while (n > 0) { s = String.fromCharCode(64 + ((n - 1) % 26 + 1)) + s; n = Math.floor((n - 1) / 26); }
  return s;
};

// ─── Static styles ────────────────────────────────────────────────────────────
const KBD = { background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: "3px", padding: "1px 5px", fontSize: "10px", fontFamily: "monospace", color: "#374151" };
const TH_NUM = { width: "40px", minWidth: "40px", background: "#cbd5e1", border: "1px solid #adb5bd", textAlign: "center", fontSize: "11px", color: "#64748b", height: "28px", position: "sticky", top: 0, zIndex: 10, boxSizing: "border-box" };
const thCol = (w, inColSel) => ({ minWidth: `${w}px`, width: `${w}px`, background: inColSel ? "#1b5e20" : "#217346", color: "#fff", border: "1px solid #1b5e20", padding: "0 6px", fontSize: "12px", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", height: "28px", position: "sticky", top: 0, zIndex: 5, boxSizing: "border-box", userSelect: "none" });
const rnTd = (ri, focR) => ({ width: "40px", minWidth: "40px", background: focR === ri ? "#dbeafe" : "#f1f5f9", border: "1px solid #d0d0d0", textAlign: "center", fontSize: "11px", color: "#64748b", height: "28px", fontWeight: focR === ri ? 700 : 400, padding: 0, position: "sticky", left: 0, zIndex: 2, userSelect: "none" });
const tdStyle = (isFoc, isSel, ri, isEditFoc = false) => ({ height: "28px", padding: isEditFoc ? 0 : "0 3px", border: isFoc ? "2px solid #1565c0" : isSel ? "1px solid #90caf9" : "1px solid #d0d0d0", background: isFoc ? "#ffffff" : isSel ? "#bbdefb" : ri % 2 === 0 ? "#ffffff" : "#f8f9fa", cursor: "cell", position: "relative", zIndex: isFoc ? 4 : isSel ? 2 : 1, boxSizing: "border-box", overflow: "hidden", verticalAlign: "middle" });

// ─── Main Component ───────────────────────────────────────────────────────────
const MultiDepartmentEntry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { firmId: sessionFirmId, branchId: sessionBranchId } = useSession();

  const [rows,        setRows]        = useState(() => Array.from({ length: 10 }, () => emptyRow(sessionFirmId, sessionBranchId)));
  const [focus,       setFocus]       = useState({ r: 0, c: 0 });
  const [anchor,      setAnchor]      = useState({ r: 0, c: 0 });
  const [editMode,    setEditMode]    = useState(false);
  const [dropdowns,   setDropdowns]   = useState({ FirmMaster: [], BranchMaster: [] });
  const [pageSize,    setPageSize]    = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading]     = useState(false);

  const rowsRef         = useRef(rows);
  const focusRef        = useRef(focus);
  const anchorRef       = useRef(anchor);
  const editModeRef     = useRef(false);
  const editModeTypeRef = useRef("typing");
  const editOrigRef     = useRef("");
  const inputRef        = useRef(null);
  const gridRef         = useRef(null);
  const focusedCellRef  = useRef(null);
  const openDropdownRef = useRef(false);
  const currentPageRef  = useRef(currentPage);

  useEffect(() => { rowsRef.current     = rows;     }, [rows]);
  useEffect(() => { focusRef.current    = focus;    }, [focus]);
  useEffect(() => { anchorRef.current   = anchor;   }, [anchor]);
  useEffect(() => { editModeRef.current = editMode; }, [editMode]);
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  const FetchAllDepartments = async () => {
    try {
      const res = await Fn_FillListData(dispatch, () => {}, "none",
        `${API_WEB_URLS.MASTER}/0/token/DepartmentMaster/Id/0`);
      if (res && res.length > 0) {
        const loaded = res.map(apiRowToGridRow);
        const blanks = Array.from({ length: 5 }, () => emptyRow(sessionFirmId, sessionBranchId));
        setRows([...loaded, ...blanks]);
        console.log(`Loaded ${loaded.length} existing departments`);
      } else {
        setRows(Array.from({ length: 10 }, () => emptyRow(sessionFirmId, sessionBranchId)));
      }
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  };

  useEffect(() => {
    const fetchAll = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          Fn_FillListData(dispatch, setDropdowns, "FirmMaster",   `${API_WEB_URLS.MASTER}/0/token/FirmMaster/Id/0`),
          Fn_FillListData(dispatch, setDropdowns, "BranchMaster", `${API_WEB_URLS.MASTER}/0/token/GetBranchByFirmId/Id/${sessionFirmId || "0"}`),
        ]);
        await FetchAllDepartments();
      } catch (error) {
        console.error("Error loading initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchAll();
  }, [dispatch, sessionFirmId]);

  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(rows.length, currentPage * pageSize);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  useEffect(() => {
    const focusRow = focus.r;
    const pageNum = Math.floor(focusRow / pageSize) + 1;
    if (pageNum !== currentPageRef.current && pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  }, [focus.r, pageSize, totalPages]);

  useEffect(() => {
    const container = gridRef.current; const cell = focusedCellRef.current;
    if (!container || !cell) return;
    const cRect = container.getBoundingClientRect(); const eRect = cell.getBoundingClientRect();
    const stickyLeft = 40;
    if (eRect.left < cRect.left + stickyLeft) container.scrollLeft -= cRect.left + stickyLeft - eRect.left;
    else if (eRect.right > cRect.right) container.scrollLeft += eRect.right - cRect.right;
    const stickyTop = 28;
    if (eRect.top < cRect.top + stickyTop) container.scrollTop -= cRect.top + stickyTop - eRect.top;
    else if (eRect.bottom > cRect.bottom) container.scrollTop += eRect.bottom - cRect.bottom;
  }, [focus]);

  useEffect(() => {
    if (editMode) {
      const el = inputRef.current; if (!el) return;
      const timer = setTimeout(() => {
        el.focus();
        const supportsSelection = el.setSelectionRange && el.tagName !== "SELECT" && el.type !== "time" && el.type !== "number";
        if (supportsSelection) { try { el.setSelectionRange(el.value?.length ?? 0, el.value?.length ?? 0); } catch (_) {} }
        if (openDropdownRef.current && el.tagName === "SELECT") {
          openDropdownRef.current = false;
          try { el.showPicker(); } catch (_) {}
        }
      }, 0);
      return () => clearTimeout(timer);
    } else { gridRef.current?.focus(); }
  }, [focus, editMode]);

  const selRange = getSelRange(focus, anchor);
  const inSel    = (r, c) => r >= selRange.r1 && r <= selRange.r2 && c >= selRange.c1 && c <= selRange.c2;
  const isFoc    = (r, c) => focus.r === r && focus.c === c;

  const ensureRows = useCallback((needed) => {
    setRows((prev) => prev.length >= needed ? prev : [...prev, ...Array.from({ length: needed - prev.length }, emptyRow)]);
  }, []);

  const gotoCell = useCallback((r, c, extendSel = false) => {
    const nr = Math.max(0, r); const nc = Math.max(0, Math.min(c, COLS.length - 1));
    if (nr >= rowsRef.current.length) ensureRows(nr + 1);
    if (!extendSel) setAnchor({ r: nr, c: nc });
    setFocus({ r: nr, c: nc }); setEditMode(false);
  }, [ensureRows]);

  const setCell = useCallback((r, key, val) => {
    setRows((prev) => prev.map((row, i) => (i === r ? { ...row, [key]: val } : row)));
  }, []);

  const handlePageChange = useCallback((newPage) => {
    const totalP = Math.max(1, Math.ceil(rowsRef.current.length / pageSize));
    if (newPage < 1 || newPage > totalP) return;
    setCurrentPage(newPage);
    const newRow = (newPage - 1) * pageSize;
    setFocus(f => ({ r: newRow, c: f.c }));
    setAnchor(a => ({ r: newRow, c: a.c }));
  }, [pageSize]);

  const handlePageSizeChange = useCallback((newSize) => {
    setPageSize(newSize);
    setCurrentPage(1);
    setFocus(f => ({ r: 0, c: f.c }));
    setAnchor(a => ({ r: 0, c: a.c }));
  }, []);

  const addNewRow = useCallback(() => {
    setRows(prev => {
      const next = [...prev, emptyRow()];
      const newIdx = next.length - 1;
      setTimeout(() => {
        setFocus({ r: newIdx, c: 0 });
        setAnchor({ r: newIdx, c: 0 });
        setEditMode(true);
      }, 0);
      return next;
    });
  }, []);

  const enterEditMode = useCallback((r, c, overwriteChar = null) => {
    const col = COLS[c];
    editOrigRef.current     = rowsRef.current[r]?.[col.key] ?? "";
    editModeTypeRef.current = overwriteChar === null ? "f2" : "typing";
    if (overwriteChar !== null && (col.type === "text" || col.type === "number" || col.type === "time"))
      setCell(r, col.key, overwriteChar);
    setEditMode(true);
  }, [setCell]);

  const displayVal = (col, val) => {
    if (col.type === "select") { const opt = (dropdowns[col.optKey] || []).find((o) => String(o.Id) === String(val)); return opt ? opt.Name : ""; }
    if (col.type === "boolean") return val === "true" ? "Yes" : "No";
    return val ?? "";
  };

  const doPaste = useCallback((text, startR, startC) => {
    if (!text) return;
    const pasted = text.replace(/\r/g, "").trimEnd().split("\n").map((l) => l.split("\t"));
    setRows((prev) => {
      const next = [...prev];
      while (next.length < startR + pasted.length) next.push(emptyRow());
      pasted.forEach((cols, dr) => cols.forEach((val, dc) => { const tc = startC + dc; if (tc < COLS.length) next[startR + dr] = { ...next[startR + dr], [COLS[tc].key]: val.trim() }; }));
      return next;
    });
  }, []);

  const doCopy = useCallback(() => {
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    const lines = [];
    for (let ri = r1; ri <= r2; ri++) { const cells = []; for (let ci = c1; ci <= c2; ci++) cells.push(String(rowsRef.current[ri]?.[COLS[ci].key] ?? "")); lines.push(cells.join("\t")); }
    navigator.clipboard?.writeText(lines.join("\n"));
  }, []);

  const clearSel = useCallback(() => {
    const { r1, r2, c1, c2 } = getSelRange(focusRef.current, anchorRef.current);
    setRows((prev) => { const next = prev.map((row) => ({ ...row })); for (let ri = r1; ri <= r2; ri++) for (let ci = c1; ci <= c2; ci++) { const col = COLS[ci]; next[ri][col.key] = col.type === "boolean" ? "false" : ""; } return next; });
  }, []);

  const selectAll = useCallback(() => { setAnchor({ r: 0, c: 0 }); setFocus({ r: rowsRef.current.length - 1, c: COLS.length - 1 }); }, []);

  const handleKey = useCallback((e) => {
    const { r, c } = focusRef.current;
    const col = COLS[c];
    const isTextLike = col.type === "text" || col.type === "number" || col.type === "time" || col.type === "date";
    const shift = e.shiftKey; const ctrl = e.ctrlKey || e.metaKey; const inEdit = editModeRef.current;
    if (ctrl) {
      if (e.key.toLowerCase() === "c") { doCopy(); return; }
      if (e.key.toLowerCase() === "v") { e.preventDefault(); navigator.clipboard?.readText().then((t) => doPaste(t, r, c)); return; }
      if (e.key.toLowerCase() === "a") { e.preventDefault(); selectAll(); return; }
      return;
    }
    if (inEdit) {
      const isF2Mode = editModeTypeRef.current === "f2";
      switch (e.key) {
        case "Escape": e.preventDefault(); setCell(r, col.key, editOrigRef.current); setEditMode(false); break;
        case "Enter":  e.preventDefault(); e.stopPropagation(); gotoCell(r + 1, c); break;
        case "Tab":    e.preventDefault(); e.stopPropagation(); if (shift) { c === 0 ? gotoCell(r - 1, COLS.length - 1) : gotoCell(r, c - 1); } else { c === COLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); } break;
        case "ArrowUp":    if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); gotoCell(r - 1, c); break;
        case "ArrowDown":  if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); gotoCell(r + 1, c); break;
        case "ArrowLeft":  if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); gotoCell(r, c - 1); break;
        case "ArrowRight": if (isF2Mode) break; e.preventDefault(); e.stopPropagation(); gotoCell(r, c + 1); break;
        default: break;
      }
      return;
    }
    switch (e.key) {
      case "Tab":    e.preventDefault(); if (shift) { c === 0 ? gotoCell(r - 1, COLS.length - 1) : gotoCell(r, c - 1); } else { c === COLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); } break;
      case "Enter":  e.preventDefault(); e.stopPropagation(); gotoCell(r + 1, c); break;
      case "F2":     e.preventDefault(); enterEditMode(r, c, null); break;
      case "ArrowUp":    e.preventDefault(); e.stopPropagation(); gotoCell(r - 1, c, shift); break;
      case "ArrowDown":  e.preventDefault(); e.stopPropagation(); gotoCell(r + 1, c, shift); break;
      case "ArrowLeft":  e.preventDefault(); e.stopPropagation(); gotoCell(r, c - 1, shift); break;
      case "ArrowRight": e.preventDefault(); e.stopPropagation(); gotoCell(r, c + 1, shift); break;
      case "Delete": case "Backspace": e.preventDefault(); clearSel(); break;
      case " ": if (col.type === "select" || col.type === "boolean") { e.preventDefault(); openDropdownRef.current = true; enterEditMode(r, c, null); } break;
      default: if (isTextLike && e.key.length === 1) enterEditMode(r, c, e.key); break;
    }
  }, [gotoCell, enterEditMode, setCell, doCopy, doPaste, clearSel, selectAll]);

  const handleGridKeyDown = useCallback((e) => {
    if (e.target !== gridRef.current) {
      const isF2Mode = editModeTypeRef.current === "f2";
      if (!isF2Mode && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) e.preventDefault();
      return;
    }
    handleKey(e);
  }, [handleKey]);

  const handleCellClick = useCallback((r, c, e) => {
    if (e.shiftKey) { setEditMode(false); setFocus({ r, c }); } else { gotoCell(r, c); }
  }, [gotoCell]);

  const delRow = (idx) => {
    setEditMode(false);
    if (rows.length === 1) { setRows([emptyRow()]); setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 }); return; }
    setRows((prev) => prev.filter((_, i) => i !== idx));
    const fix = (pos) => ({ ...pos, r: Math.max(0, pos.r >= idx ? pos.r - 1 : pos.r) });
    setFocus(fix); setAnchor(fix);
  };

  const handleSave = async () => {
    const valid = rows.filter((r) => r.Name?.trim());
    if (!valid.length) { alert("Please enter at least one Department Name."); return; }

    // Check for duplicate names
    const nameCount = {};
    valid.forEach((r) => { const n = r.Name.trim().toLowerCase(); nameCount[n] = (nameCount[n] || 0) + 1; });
    if (Object.values(nameCount).some((c) => c > 1)) {
      alert("Duplicate Department Names detected! Please fix duplicate entries.");
      return;
    }

    setIsLoading(true);

    try {
      const payload = valid.map((row) => {
        const basic = { ...row };
        const BOOL_COLS = ["IsSundayInclude", "IsDirectWages", "IsNotInUse"];
        BOOL_COLS.forEach(k => { if (k in basic) basic[k] = basic[k] === "true" || basic[k] === true; });
        basic.Id = basic.Id || 0;
        basic.F_FirmMaster = parseInt(basic.F_FirmMaster || sessionFirmId) || 0;
        basic.F_BranchMaster = parseInt(basic.F_BranchMaster || sessionBranchId) || 0;
        basic.Ordering = parseInt(basic.Ordering) || 0;
        return basic;
      });

      console.log("=== Multi Department Entry — Payload ===");
      console.log(JSON.stringify(payload, null, 2));

      const formData = new FormData();
      formData.append("DepartmentJson", JSON.stringify(payload));
      formData.append("UserId", 0);

      const res = await Fn_AddEditData(
        dispatch,
        () => {}, // setState not needed here
        { arguList: { id: 0, formData } },
        'DepartmentMasterBulk/0/token',
        true,
        "memberid",
        navigate,
        "#"
      );

      console.log("Save response:", res);
      if (res && res?.id > 0) {
        toast.success(`${valid.length} department(s) saved successfully!`, { autoClose: 3000 });
        setRows(Array.from({ length: 10 }, emptyRow));
        setFocus({ r: 0, c: 0 });
        setAnchor({ r: 0, c: 0 });
        setEditMode(false);
      } else {
        toast.error("Save failed. Please try again.", { autoClose: 4000 });
      }
    } catch (error) {
      console.error("Error saving departments:", error);
      toast.error("Error occurred while saving. Please try again.", { autoClose: 4000 });
    } finally {
      setIsLoading(false);
    }
  };

  const renderInput = (r, c) => {
    const col = COLS[c]; const val = rows[r]?.[col.key] ?? "";
    const shared = {
      ref: inputRef,
      style: { width: "100%", height: "100%", border: "none", outline: "none", background: "transparent", padding: "0 4px", fontSize: "13px", boxSizing: "border-box" },
      onKeyDown: handleKey,
      onPaste: (e) => { const text = e.clipboardData?.getData("text"); if (text && (text.includes("\t") || text.includes("\n"))) { e.preventDefault(); doPaste(text, r, c); } },
    };
    if (col.type === "select") return (
      <select {...shared} value={val} onChange={(e) => { setCell(r, col.key, e.target.value); c === COLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); }}>
        <option value="">{col.placeholder ?? "-- Select --"}</option>
        {(dropdowns[col.optKey] || []).map((o) => <option key={o.Id} value={o.Id}>{o.Name}</option>)}
      </select>
    );
    if (col.type === "boolean") return (
      <select {...shared} value={val} onChange={(e) => { setCell(r, col.key, e.target.value); c === COLS.length - 1 ? gotoCell(r + 1, 0) : gotoCell(r, c + 1); }}>
        <option value="">-- Select --</option>
        <option value="false">No</option>
        <option value="true">Yes</option>
      </select>
    );
    return <input {...shared} type={col.type === "number" ? "number" : col.type === "time" ? "time" : col.type === "date" ? "date" : "text"} value={val} onChange={(e) => setCell(r, col.key, e.target.value)} />;
  };

  const filledCount = rows.filter((r) => r.Name?.trim()).length;
  const isMultiSel  = selRange.r2 > selRange.r1 || selRange.c2 > selRange.c1;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "calc(100vh - 80px)", overflow: "hidden" }}>
      <PageTitle activeMenu="Masters" motherMenu="Masters" pageContent="Multi Department Entry" />

      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: "8px", padding: "6px 12px", background: "#e8f5e9", borderBottom: "2px solid #217346", flexShrink: 0 }}>
        <span style={{ fontSize: "14px", fontWeight: 700, color: "#217346", marginRight: "4px" }}>
          <i className="fas fa-table me-2"></i>Multi Department Entry
        </span>
        <button className="btn btn-sm btn-outline-success" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={addNewRow}>
          <i className="fas fa-plus me-1"></i>Add Row
        </button>
        <button className="btn btn-sm btn-outline-danger" style={{ fontSize: "12px", padding: "2px 10px" }}
          onClick={() => { if (window.confirm("Saara data clear ho jayega. Confirm?")) { setRows(Array.from({ length: 10 }, emptyRow)); setFocus({ r: 0, c: 0 }); setAnchor({ r: 0, c: 0 }); setEditMode(false); } }}>
          <i className="fas fa-trash me-1"></i>Clear All
        </button>
        <button className="btn btn-sm btn-outline-secondary" style={{ fontSize: "12px", padding: "2px 10px" }} onClick={() => navigate("/DepartmentMaster")}>
          <i className="fas fa-arrow-left me-1"></i>Back
        </button>
        <span style={{ fontSize: "11px", color: "#444", marginLeft: "4px" }}>
          <kbd style={KBD}>↑↓←→</kbd> Navigate &nbsp;
          <kbd style={KBD}>F2</kbd>/<kbd style={KBD}>DblClick</kbd> Edit &nbsp;
          <kbd style={KBD}>Type</kbd> Overwrite &nbsp;
          <kbd style={KBD}>Esc</kbd> Restore &nbsp;
          <kbd style={KBD}>Shift+Arrow</kbd> Select &nbsp;
          <kbd style={KBD}>Ctrl+C/V</kbd> Copy/Paste &nbsp;
          <kbd style={KBD}>Del</kbd> Clear
        </span>
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "10px" }}>
          <span style={{ fontSize: "12px", fontWeight: 700, color: "#217346" }}>{filledCount} / {rows.length} rows filled</span>
          <button className="btn btn-sm btn-success" style={{ padding: "3px 14px", fontSize: "13px" }} onClick={handleSave} disabled={isLoading}>
            {isLoading ? <i className="fas fa-spinner fa-spin me-1"></i> : <i className="fas fa-save me-1"></i>}Save
          </button>
        </div>
      </div>

      {/* ─── Pagination Toolbar ─── */}
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "6px 16px",
        background: "#e8f5e9",
        borderBottom: "1px solid #ced4da",
        fontSize: "12px",
        flexShrink: 0,
        userSelect: "none"
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ fontWeight: 600, color: "#217346" }}>Show:</span>
          <select
            value={pageSize}
            onChange={e => handlePageSizeChange(parseInt(e.target.value))}
            style={{ padding: "2px 6px", borderRadius: "4px", border: "1px solid #ced4da", fontSize: "12px", outline: "none", background: "#fff", cursor: "pointer" }}
          >
            <option value={10}>10 rows</option>
            <option value={20}>20 rows</option>
            <option value={50}>50 rows</option>
            <option value={100}>100 rows</option>
          </select>
          <span style={{ color: "#555" }}>
            Showing <strong>{rows.length > 0 ? startIndex + 1 : 0}</strong> - <strong>{endIndex}</strong> of <strong>{rows.length}</strong> rows
          </span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <button
            onClick={() => handlePageChange(1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            « First
          </button>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === 1 ? "default" : "pointer" }}
          >
            ‹ Prev
          </button>
          <span style={{ padding: "0 8px", fontWeight: 700, color: "#217346" }}>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Next ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={currentPage === totalPages}
            className="btn btn-xs btn-outline-success"
            style={{ padding: "2px 8px", fontSize: "11px", cursor: currentPage === totalPages ? "default" : "pointer" }}
          >
            Last »
          </button>
        </div>
      </div>

      <div ref={gridRef} tabIndex={0} onKeyDown={handleGridKeyDown} style={{ flex: 1, overflowX: "auto", overflowY: "auto", outline: "none", background: "#fff" }}>
        <table style={{ borderCollapse: "collapse", tableLayout: "fixed", fontSize: "13px", userSelect: "none" }}>
          <thead>
            <tr>
              <th style={TH_NUM}></th>
              {COLS.map((col, ci) => (
                <th key={ci} style={{ ...thCol(col.w, ci >= selRange.c1 && ci <= selRange.c2), minWidth: `${col.w}px`, width: `${col.w}px` }}>
                  <span style={{ marginRight: "4px", opacity: 0.6, fontWeight: 400, fontSize: "10px" }}>{colLetter(ci)}</span>
                  {col.label}
                </th>
              ))}
              <th style={{ ...TH_NUM, width: "36px", minWidth: "36px" }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(startIndex, endIndex).map((row, localRi) => {
              const ri = startIndex + localRi;
              return (
                <tr key={ri}>
                  <td style={rnTd(ri, focus.r)}>{ri + 1}</td>
                  {COLS.map((col, ci) => {
                    const focused = isFoc(ri, ci); const selected = inSel(ri, ci);
                    return (
                      <td key={ci} ref={focused ? focusedCellRef : null}
                        style={{ ...tdStyle(focused, selected, ri, focused && editMode), minWidth: `${col.w}px`, width: `${col.w}px` }}
                        onMouseDown={(e) => handleCellClick(ri, ci, e)}
                        onDoubleClick={() => enterEditMode(ri, ci, null)}
                      >
                        {focused && editMode ? renderInput(ri, ci) : (() => {
                          const dv = displayVal(col, row[col.key]);
                          const isEmptySel = !dv && (col.type === "select" || col.type === "boolean");
                          return (
                            <span style={{ display: "block", padding: "0 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", lineHeight: "26px", color: isEmptySel ? "#b0b7c3" : row[col.key] ? "#212529" : "#ccc", fontWeight: focused ? 600 : 400, fontStyle: isEmptySel ? "italic" : "normal" }}>
                              {isEmptySel ? (col.placeholder?.replace(/^-- | --$/g, "") ?? "Select...") : (dv || "\u00A0")}
                            </span>
                          );
                        })()}
                      </td>
                    );
                  })}
                  <td style={{ width: "36px", minWidth: "36px", height: "28px", border: "1px solid #d0d0d0", textAlign: "center", background: "#fafafa", padding: 0 }}>
                    <button onClick={() => delRow(ri)} title="Row delete" style={{ border: "none", background: "none", color: "#dc3545", fontSize: "18px", lineHeight: 1, cursor: "pointer", width: "100%", height: "100%", fontWeight: 700 }}>×</button>
                  </td>
                </tr>
              );
            })}
            <tr>
              <td colSpan={COLS.length + 2} onClick={addNewRow}
                style={{ border: "1px dashed #b0b0b0", textAlign: "center", color: "#aaa", fontSize: "12px", cursor: "pointer", height: "28px", background: "#fafafa", userSelect: "none" }}>
                ＋ Click here to add a new row
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ flexShrink: 0, padding: "3px 12px", background: "#e8f5e9", borderTop: "1px solid #a5d6a7", fontSize: "12px", color: "#555", display: "flex", gap: "24px", alignItems: "center" }}>
        <span>Cell: <strong style={{ color: "#217346" }}>{colLetter(focus.c)}{focus.r + 1}</strong></span>
        {isMultiSel && (
          <span>Selection: <strong>{colLetter(selRange.c1)}{selRange.r1 + 1} → {colLetter(selRange.c2)}{selRange.r2 + 1}</strong> ({selRange.r2 - selRange.r1 + 1}R × {selRange.c2 - selRange.c1 + 1}C)</span>
        )}
        <span style={{ marginLeft: "auto", color: "#666" }}>
          <i className="fas fa-info-circle me-1" style={{ color: "#217346" }}></i>
          Copy from Excel and paste directly with Ctrl+V — multi-cell auto-fill enabled
        </span>
      </div>
    </div>
  );
};

export default MultiDepartmentEntry;
